import { useState } from 'react';
import { useNavigate } from 'react-router';
import { DownloadIcon, ShieldIcon, TrashIcon, UsersIcon } from '@repo/atomic-components';
import {
  useLazyGetGeneralFeedbackExportQuery,
  useLazyGetMessageFeedbackExportQuery,
  useLazyGetToolkitPromptRequestsExportQuery,
} from '@/api';
import { AppRoutes } from '@/constants';
import { downloadBlob } from '@/utils/downloadBlob';
import { SettingsActionButton } from '../SettingsActionButton';
import { useSettingsHub } from '../SettingsHubContext';
import { BulkDeleteMattersModal } from './BulkDeleteMattersModal';

/** Admin links go to full pages, so close the hub when navigating. */
const useNavigateAndClose = () => {
  const navigate = useNavigate();
  const { closeSettings } = useSettingsHub();

  return (route: AppRoutes) => {
    closeSettings();
    navigate(`/${route}`);
  };
};

export const UserManagementControl = () => {
  const navigateAndClose = useNavigateAndClose();

  return (
    <SettingsActionButton Icon={UsersIcon} onClick={() => navigateAndClose(AppRoutes.UserManagement)}>
      Manage users
    </SettingsActionButton>
  );
};

export const AdminDashboardControl = () => {
  const navigateAndClose = useNavigateAndClose();

  return (
    <SettingsActionButton Icon={ShieldIcon} onClick={() => navigateAndClose(AppRoutes.AdminTools)}>
      Open admin dashboard
    </SettingsActionButton>
  );
};

export const FeedbackExportsControl = () => {
  const [triggerGetMessageFeedbackExport] = useLazyGetMessageFeedbackExportQuery();
  const [triggerGetGeneralFeedbackExport] = useLazyGetGeneralFeedbackExportQuery();
  const [triggerGetToolkitPromptRequestsExport] = useLazyGetToolkitPromptRequestsExportQuery();

  const exports = [
    {
      label: 'AI response feedback',
      fileName: 'message-feedback.csv',
      trigger: triggerGetMessageFeedbackExport,
    },
    {
      label: 'Fletch UX feedback',
      fileName: 'general-feedback.csv',
      trigger: triggerGetGeneralFeedbackExport,
    },
    {
      label: 'Toolkit prompt requests',
      fileName: 'toolkit-prompt-requests.csv',
      trigger: triggerGetToolkitPromptRequestsExport,
    },
  ];

  return (
    <div className="flex flex-wrap gap-2">
      {exports.map(({ label, fileName, trigger }) => (
        <SettingsActionButton
          key={fileName}
          Icon={DownloadIcon}
          onClick={() =>
            trigger()
              .unwrap()
              .then((blob) => downloadBlob(blob, fileName))
              .catch((error) => console.error(`Failed to download ${fileName}:`, error))
          }
        >
          {label}
        </SettingsActionButton>
      ))}
    </div>
  );
};

/** Opens over the hub rather than navigating away, so the admin comes back to the Admin tab. */
export const BulkDeleteMattersControl = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);

  return (
    <>
      <SettingsActionButton Icon={TrashIcon} onClick={() => setIsModalVisible(true)}>
        Bulk delete matters
      </SettingsActionButton>
      <BulkDeleteMattersModal visible={isModalVisible} onHide={() => setIsModalVisible(false)} />
    </>
  );
};
